import React, { useState, useEffect, useRef } from "react";
import TextField from "@/common/components/TextField";
import { ArrowLeftToLine, ArrowRightToLine, Search } from "lucide-react";
import IconButton from "@/common/components/IconButton";
import { useAppDetails } from "@/common/hooks/useAppDetails";
import Skeleton from "@/common/components/Skeleton";
import useOnlineStatus from "@/common/hooks/useOnlineStatus";
import { twMerge } from "tailwind-merge";
import { useMenu } from "@/common/stores/menu";

const LoadingSidebar = () => {
  const { collapse, updateCollapse } = useMenu();
  const { commitSha } = useAppDetails();
  const [skeletonCount, setSkeletonCount] = useState(0);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const { isOnline } = useOnlineStatus();

  useEffect(() => {
    const calculateSkeletonCount = () => {
      if (sidebarRef.current && bottomRef.current) {
        const sidebarHeight = sidebarRef.current.clientHeight;
        const bottomHeight = bottomRef.current.offsetHeight;
        const availableHeight = sidebarHeight - bottomHeight - 240;
        const skeletonHeight = 28 + 18;
        const count = Math.max(0, Math.floor(availableHeight / skeletonHeight));

        setSkeletonCount(count);
      }
    };
    calculateSkeletonCount();
    window.addEventListener("resize", calculateSkeletonCount);
    return () => {
      window.removeEventListener("resize", calculateSkeletonCount);
    };
  }, []);

  return (
    <div
      ref={sidebarRef}
      className={twMerge(
        "relative h-screen w-[283px] left-0 bg-white-200 shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.2)] transition-all duration-150",
        !isOnline && 'mt-5 h-[calc(100vh-20px)]',
        collapse && 'w-[88px]',
      )}
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-4">
          <div className={twMerge('flex flex-col gap-4 my-2 items-center', !collapse && 'hidden')}>
            <IconButton Icon={<ArrowRightToLine strokeWidth={1} />} onClick={() => updateCollapse(false)} />

            <img src="/ReducedLogo.svg" className="w-[18px] h-auto" />
          </div>

          <div className={twMerge("flex justify-between pt-6 px-6", collapse && 'hidden')}>
            <img src="/Logo.svg" className="w-[83px] h-auto" />
            <IconButton Icon={<ArrowLeftToLine strokeWidth={1} />} onClick={() => updateCollapse(true)} />
          </div>

          <div className={twMerge("px-6", collapse && 'hidden')}>
            <TextField
              LeftIcon={<Search className="-translate-y-1 translate-x-1" size="18px" />}
              placeholder="Pesquisar"
              className="text-xs"
            />
          </div>
        </div>

        <div className="w-full space-y-1 text-white-800 px-8 py-4">
          {Array(skeletonCount)
            .fill(0)
            .map((_, index) => {
              const randomWidth = `${Math.floor(Math.random() * (100 - 50 + 1) + 50)}%`;
              return (
                <React.Fragment key={index}>
                  <Skeleton width={!collapse ? randomWidth : 28} height={28} />
                  <div className="h-[18px]" />
                </React.Fragment>
              );
            })}
        </div>
      </div>

      <div
        ref={bottomRef}
        className={twMerge(
          "absolute w-full bottom-0 p-4 flex gap-4 flex-col bg-white-200 shadow-[4px_0px_16px_0px_rgba(0,0,0,0.5)]",
          !collapse && 'h-[123px]',
          collapse && 'items-center h-[156px] ',
        )}
      >
        {collapse ? (
          <>
            <Skeleton className="size-8 rounded-full" />

            <Skeleton width={28} height={28} />
          </>
        ) : (
          <div className="flex justify-between items-center">
            <div className="flex gap-2 items-center">
              <Skeleton circle width={36} height={36} />

              <div className="flex flex-col gap-3">
                <Skeleton width={132} height={20} />

                <Skeleton width={120} height={16} />
              </div>
            </div>

            <Skeleton width={36} height={36} />
          </div>
        )}

        {collapse ? (
          <img src="/ReducedAvantsoft.svg" className="h-[20px]" />
        ) : (
          <div className="flex items-end justify-between">
            <div className="flex flex-col items-start">
              <span className="text-[9px] italic font-light">Desenvolvido por</span>
              <img src="/Avantsoft.svg" className="h-[12px]" />
            </div>

            <p className="font-light text-[10px] italic">
              Versão: <span className="font-bold not-italic">{commitSha}</span>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoadingSidebar;
